<template>
  <div id="chart-settings-container">
    <h3 class="chart-settings-section-title">
      <span>
        <i class="far fa-fw fa-sliders-h-square"></i>
        ATTRIBUTES
      </span>
    </h3>
    <div id="attributes-container">
      <div>
        <div class="ui-mini-container-row">
          <span class="ui-mini-container">
            <ChartTypeMenu :is-inline="true" label-text="CHART TYPE" html-id="chart-type-menu-lower" />
          </span>
        </div>
        <div class="ui-mini-container-row" v-show="chartStore.chartType === CHART_TYPE_OPTION_PERFORMANCE_PERCENT.value">
          <span class="ui-mini-container">
            <UseMainChartSymbolAsBaselineCheckbox html-id="chart-settings-use-main-chart-symbol-as-performance-percent-chart-baseline-checkbox" :is-inline="false" />
          </span>
        </div>
        <div class="ui-mini-container-row" v-show="chartStore.chartType === CHART_TYPE_OPTION_KAGI.value">
          <span class="ui-mini-container">
            <KagiReversalUnitMenu html-id="chart-settings-kagi-reversal-unit-menu" />
          </span>
          <span class="ui-mini-container">
            <KagiReversalInput html-id="chart-settings-kagi-reversal-input" :is-at-least-one-hundred-pixels-wide="true" />
          </span>
          <span class="ui-mini-container">
            <KagiPriceFieldMenu html-id="chart-settings-kagi-price-field-menu" />
          </span>
        </div>
        <div class="ui-mini-container-row" v-show="chartStore.chartType === CHART_TYPE_OPTION_RENKO.value">
          <span class="ui-mini-container">
            <RenkoBoxSizeUnitMenu html-id="chart-settings-renko-box-size-menu" />
          </span>
          <span class="ui-mini-container">
            <RenkoBoxSizeInput html-id="chart-settings-renko-box-size-input" :is-at-least-one-hundred-pixels-wide="true" />
          </span>
          <span class="ui-mini-container">
            <RenkoPriceFieldMenu html-id="chart-settings-renko-price-field-menu" />
          </span>
        </div>
        <div class="ui-mini-container-row">
          <span class="ui-mini-container">
            <PeriodMenu :is-inline="true" label-text="PERIOD" html-id="period-menu-lower" :should-automatically-disable-intraday-period-options="false" :is-compact="true" :is-white-and-grey="true" />
          </span>
          <br class="chart-settings-range-ui-line-break" />
          <span class="ui-mini-container">
            <RangeMenu :is-inline="true" label-text="RANGE" html-id="range-menu-lower" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.shouldDisplayRangeBarAndGapMenus">
            <RangeBarMenu html-id="chart-settings-range-bar-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.shouldDisplayRangeBarAndGapMenus">
            <RangeGapMenu html-id="chart-settings-range-gap-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.shouldDisplayRangeYearMonthAndDayMenus">
            <RangeYearsMenu v-model:range-years="chartStore.rangeYears" html-id="chart-settings-range-years-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.shouldDisplayRangeYearMonthAndDayMenus">
            <RangeMonthsMenu v-model:range-months="chartStore.rangeMonths" html-id="chart-settings-range-months-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.shouldDisplayRangeYearMonthAndDayMenus">
            <RangeDaysMenu v-model:range-days="chartStore.rangeDays" html-id="chart-settings-range-days-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.shouldDisplayRangeDatePickers">
            <DatePicker
              v-model:date-string="chartStore.rangeStartDate"
              label-text="Start"
              placeholder-text="Select Date"
              html-id="chart-settings-start-date-date-picker-input"
            />
          </span>
          <span class="ui-mini-container" v-show="chartStore.shouldDisplayRangeDatePickers">
            <DatePicker
              v-model:date-string="chartStore.rangeEndDate"
              label-text="End"
              placeholder-text="Select Date"
              html-id="chart-settings-end-date-date-picker-input"
            />
          </span>
          <span class="ui-mini-container">
            <ExtraBarsInput html-id="chart-settings-extra-bars-input" />
          </span>
        </div>
        <div class="ui-mini-container-row ui-mini-container-row-right-aligned" v-show="chartStore.shouldDisplayRangeDatePickers && clientWidth > 385">
          <span class="ui-mini-container" ref="rangeSliderElementContainer"></span>
        </div>
        <div class="ui-mini-container-row">
          <span class="ui-mini-container">
            <ChartSizeMenu html-id="chart-settings-chart-size-menu" />
          </span>
          <span class="ui-mini-container">
            <ColorSchemeMenu html-id="chart-settings-color-scheme-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
            <UpColorMenu html-id="chart-settings-up-color-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
            <DownColorMenu html-id="chart-settings-down-color-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
            <OpacityMenu html-id="chart-settings-opacity-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value">
            <LegendsMenu html-id="chart-settings-perf-chart-ui-legends-menu" />
          </span>
          <span class="ui-mini-container" v-show="chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value">
            <GridStyleMenu html-id="chart-settings-perf-chart-ui-grid-style-menu" />
          </span>
        </div>
        <div class="ui-mini-container-row" v-show="chartStore.chartSize === CHART_SIZE_MENU_OPTION_CUSTOM.title">
          <span class="ui-mini-container">
            <CustomChartWidthInput html-id="chart-settings-custom-chart-width-input" />
          </span>
          <span class="ui-mini-container">
            <CustomChartHeightInput html-id="chart-settings-custom-chart-height-input" />
          </span>
        </div>
        <div class="ui-mini-container-row" v-show="chartStore.colorScheme === COLOR_SCHEME_CUSTOM.value">
          <span class="ui-mini-container">
            <ColorPicker
              v-model:color="chartStore.customColorSchemeBackgroundColor"
              :default-color="CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_BACKGROUND"
              label-text="Custom Background Color"
            />
          </span>
          <span class="ui-mini-container">
            <ColorPicker
              v-model:color="chartStore.customColorSchemeGridColor"
              :default-color="CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_GRID"
              label-text="Custom Grid Color"
            />
          </span>
          <span class="ui-mini-container">
            <ColorPicker
              v-model:color="chartStore.customColorSchemeTextColor"
              :default-color="CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_TEXT"
              label-text="Custom Text Color"
            />
          </span>
          <span class="ui-mini-container">
            <ColorPicker
              v-model:color="chartStore.customColorSchemeIndicatorColor"
              :default-color="CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_INDICATOR"
              label-text="Custom Indicator Color"
            />
          </span>
        </div>
        <div class="ui-mini-container-row" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
          <span class="ui-mini-container">
            <VolumeMenu html-id="chart-settings-non-perf-chart-ui-volume-menu" />
          </span>
          <span class="ui-mini-container">
            <LegendsMenu html-id="chart-settings-non-perf-chart-ui-legends-menu" />
          </span>
          <span class="ui-mini-container">
            <GridStyleMenu html-id="chart-settings-non-perf-chart-ui-grid-style-menu" />
          </span>
        </div>
      </div>
      <div>
        <label class="sc3-form-label sc3-form-label-inline sc3-form-label-small" for="chart-settings-checkboxes">SCALE &amp; DISPLAY SETTINGS</label>
        <br />
        <fieldset
          id="chart-settings-checkboxes"
          class="ui-mini-container-row ui-mini-container-row-last chart-settings-checkbox-container"
          :class="{ 'chart-settings-checkbox-container-perf-chart': chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value }"
        >
          <div class="ui-mini-container ui-mini-checkbox-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
            <LogScaleCheckbox html-id="chart-settings-non-perf-chart-ui-log-scale-checkbox" />
            <ArithmeticScaleCheckbox html-id="chart-settings-non-perf-chart-ui-arithmetic-scale-checkbox" />
          </div>
          <div class="ui-mini-container ui-mini-checkbox-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
            <SolidCandlesCheckbox html-id="chart-settings-non-perf-chart-ui-solid-candles-checkbox" />
            <HollowCandlesCheckbox html-id="chart-settings-non-perf-chart-ui-hollow-candles-checkbox" />
          </div>
          <div class="ui-mini-container ui-mini-checkbox-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
            <AdjustForDividendsCheckbox html-id="chart-settings-non-perf-chart-ui-adjust-for-dividends-checkbox" />
            <InvertMainPriceScaleCheckbox html-id="chart-settings-non-perf-chart-ui-invert-main-price-scale-checkbox" />
          </div>
          <div class="ui-mini-container ui-mini-checkbox-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
            <FullQuoteCheckbox html-id="chart-settings-non-perf-chart-ui-full-quote-checkbox" />
            <ExtendedHoursCheckbox html-id="chart-settings-non-perf-chart-ui-extended-hours-checkbox" />
          </div>
          <div class="ui-mini-container ui-mini-checkbox-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
            <PriceLabelsCheckbox html-id="chart-settings-non-perf-chart-ui-price-labels-checkbox" />
            <YAxisLabelsCheckbox html-id="chart-settings-non-perf-chart-ui-y-axis-labels-checkbox" />
          </div>
          <div class="ui-mini-container ui-mini-checkbox-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
            <ZoomThumbnailCheckbox html-id="chart-settings-non-perf-chart-ui-zoom-thumbnail-checkbox" />
            <ColorVolumeCheckbox html-id="chart-settings-non-perf-chart-ui-color-volume-checkbox" />
          </div>
          <div class="ui-mini-container ui-mini-checkbox-container" v-show="chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value">
            <AdjustForDividendsCheckbox html-id="chart-settings-perf-chart-ui-adjust-for-dividends-checkbox" />
          </div>
          <div class="ui-mini-container ui-mini-checkbox-container" v-show="chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value">
            <YAxisLabelsCheckbox html-id="chart-settings-perf-chart-ui-y-axis-labels-checkbox" />
          </div>
          <div class="ui-mini-container ui-mini-checkbox-container" v-show="chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value">
            <ZoomThumbnailCheckbox html-id="chart-settings-perf-chart-ui-zoom-thumbnail-checkbox" />
          </div>
        </fieldset>
      </div>
    </div>
  </div>
  <div id="indicators-and-overlays-container">
    <h3 class="chart-settings-section-title" v-show="chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value">
      <span>
        <i class="far fa-fw fa-balance-scale-left"></i>
        PERFCHART SYMBOLS
      </span>
      <button
        class="sc3-button sc3-button-small sc3-button-link clear-all-button"
        type="button"
        @click="clearAllPerfChartSymbols()"
      >
        <i class="fas fa-fw fa-ban"></i>
        &nbsp;Clear Comparison Symbols
      </button>
    </h3>
    <div class="drag-n-drop-container" v-show="chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value" :class="{ 'drag-n-dropping': isDraggingAPerfChartSymbol }">
      <div class="perf-chart-symbols">
        <div class="perf-chart-symbol-row">
          <PerfChartMainSymbolInput />
          <UseMainChartSymbolAsBaselineCheckbox html-id="chart-settings-use-main-chart-symbol-as-perf-chart-baseline-checkbox" :is-inline="true" />
        </div>
        <div
          v-for="(perfChartSymbol, index) in perfChartSymbols"
          :key="perfChartSymbol.id"
          class="perf-chart-symbol-row"
          :class="{ 'row-being-dragged': perfChartSymbol.id === mouseDownId }"
        >
          <div
            v-if="isDraggingAPerfChartSymbol && perfChartSymbol.id !== mouseDownId && perfChartSymbol.id !== mouseDownIdBelow && index === 0"
            class="drop-zone top-drop-zone top-drop-zone-extension"
            @mouseup="insertRowAtIndex(index)"
          ></div>
          <div
            v-if="isDraggingAPerfChartSymbol && perfChartSymbol.id !== mouseDownId && perfChartSymbol.id !== mouseDownIdBelow"
            class="drop-zone top-drop-zone"
            @mouseup="insertRowAtIndex(index)"
          ></div>
          <div
            v-if="isDraggingAPerfChartSymbol && perfChartSymbol.id !== mouseDownId && perfChartSymbol.id !== mouseDownIdAbove && index === perfChartSymbols.length - 1"
            class="drop-zone bottom-drop-zone bottom-drop-zone-extension"
            @mouseup="insertRowAtIndex(index + 1)"
          ></div>
          <div
            v-if="isDraggingAPerfChartSymbol && perfChartSymbol.id !== mouseDownId && perfChartSymbol.id !== mouseDownIdAbove"
            class="drop-zone bottom-drop-zone"
            @mouseup="insertRowAtIndex(index + 1)"
          ></div>
          <span
            class="drag-n-drop-handle"
            :class="{ 'disabled': perfChartSymbols.length < 2 }"
            @mousedown="handleDragNDropMouseDownPerfChartSymbol(perfChartSymbol.id, $event)"
            tabindex="-1"
            title="Drag to reposition symbol"
          ><i class="fas fa-fw fa-line-height" tabindex="-1"></i></span>
          <PerfChartSymbolInput
            :id="`${perfChartSymbol.id}`"
            :should-show-label="index === 0"
            v-model:perf-chart-symbol="perfChartSymbol.symbol"
            :perf-chart-symbols="perfChartSymbols"
            @perf-chart-symbol-input-changed="handlePerfChartSymbolInputChanged"
          />
          <ReorderButton
            :id="perfChartSymbol.id"
            :is-up="true"
            :is-first-row="index === 0"
            @reorder="movePerfChartSymbolUp"
          />
          <ReorderButton
            :id="perfChartSymbol.id"
            :is-up="false"
            :is-first-row="index === 0"
            @reorder="movePerfChartSymbolDown"
          />
          <button
            v-show="perfChartSymbol.symbol"
            title="Remove Symbol"
            type="button"
            class="sc3-button sc3-button-small sc3-button-red sc3-button-square-medium-height"
            :class="{
              'sc3-button-bottom-aligned': index === 0,
              'sc3-button-top-aligned': index > 0
            }"
            @click="removePerfChartSymbol(perfChartSymbol.id)"
          ><i class="fas fa-fw fa-trash-alt"></i></button>
        </div>
      </div>
    </div>
    <h3 class="chart-settings-section-title" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
      <span>
        <i class="fas fa-fw fa-wave-sine fa-flip-horizontal"></i>
        OVERLAYS
      </span>
      <button
        class="sc3-button sc3-button-small sc3-button-link clear-all-button"
        type="button"
        @click="clearAllOverlays()"
      >
        <i class="fas fa-fw fa-ban"></i>
        &nbsp;Clear All Overlays
      </button>
    </h3>
    <div class="drag-n-drop-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value" :class="{ 'drag-n-dropping': isDraggingAnOverlay }">
      <div class="overlays">
        <div
          v-for="(overlay, index) in overlays"
          :key="overlay.id"
          class="overlay-row"
          :class="{ 'row-being-dragged': overlay.id === mouseDownId }"
        >
          <div
            v-if="isDraggingAnOverlay && overlay.id !== mouseDownId && overlay.id !== mouseDownIdBelow && index === 0"
            class="drop-zone top-drop-zone top-drop-zone-extension"
            @mouseup="insertRowAtIndex(index)"
          ></div>
          <div
            v-if="isDraggingAnOverlay && overlay.id !== mouseDownId && overlay.id !== mouseDownIdBelow"
            class="drop-zone top-drop-zone"
            @mouseup="insertRowAtIndex(index)"
          ></div>
          <div
            v-if="isDraggingAnOverlay && overlay.id !== mouseDownId && overlay.id !== mouseDownIdAbove && index === overlays.length - 1"
            class="drop-zone bottom-drop-zone bottom-drop-zone-extension"
            @mouseup="insertRowAtIndex(index + 1)"
          ></div>
          <div
            v-if="isDraggingAnOverlay && overlay.id !== mouseDownId && overlay.id !== mouseDownIdAbove"
            class="drop-zone bottom-drop-zone"
            @mouseup="insertRowAtIndex(index + 1)"
          ></div>
          <span
            class="drag-n-drop-handle"
            :class="{ 'disabled': overlays.length < 2 }"
            @mousedown="handleDragNDropMouseDownOverlay(overlay.id, $event)"
            tabindex="-1"
            title="Drag to reposition overlay"
          ><i class="fas fa-fw fa-line-height" tabindex="-1"></i></span>
          <OverlayMenu
            :is-indicator-overlay-menu="false"
            :id="overlay.id"
            :should-show-label="index === 0"
            v-model:overlay-name="overlay.overlayName"
            indicator-name=""
            :overlays="overlays"
            @overlay-menu-changed="handleOverlayMenuChanged"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
          />
          <ParametersInput
            :id="`${overlay.id}`"
            :should-show-label="index === 0"
            v-model:parameters="overlay.parameters"
            :is-overlay-parameters="true"
            :is-indicator-parameters="false"
            :is-indicator-overlay-parameters="false"
            :indicator-or-overlay-name="overlay.overlayName"
            @parameters-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
            :is-at-least-one-overlay-present="isAtLeastOneOverlayPresent"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
            :is-at-least-one-overlay-of-indicator-present="isAtLeastOneOverlayOfIndicatorPresent"
          />
          <ReorderButton
            :id="overlay.id"
            :is-up="true"
            :is-first-row="index === 0"
            @reorder="moveOverlayUp"
          />
          <ReorderButton
            :id="overlay.id"
            :is-up="false"
            :is-first-row="index === 0"
            @reorder="moveOverlayDown"
          />
          <StyleMenu
            :id="overlay.id"
            :should-show-label="index === 0"
            :is-at-least-one-non-volume-by-price-overlay-present="isAtLeastOneNonVolumeByPriceOverlayPresent"
            v-model:style="overlay.overlayStyle"
            :overlay-name="overlay.overlayName"
            @style-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
          />
          <ColorMenu
            :is-indicator-color-menu="false"
            :id="overlay.id"
            :should-show-label="index === 0"
            :is-at-least-one-non-volume-by-price-overlay-present="isAtLeastOneNonVolumeByPriceOverlayPresent"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
            v-model:color="overlay.overlayColor"
            :indicator-or-overlay-name="overlay.overlayName"
            @color-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
          />
          <IndicatorOpacityMenu
            :id="overlay.id"
            :should-show-label="index === 0"
            v-model:opacity="overlay.overlayOpacity"
            :indicator-or-overlay-name="overlay.overlayName"
            @opacity-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
            :is-overlay-opacity-menu="true"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
            :is-at-least-one-overlay-present="isAtLeastOneOverlayPresent"
          />
          <button
            v-show="overlay.overlayName !== NONE"
            title="Remove Overlay"
            type="button"
            class="sc3-button sc3-button-small sc3-button-red sc3-button-square-medium-height"
            :class="{
              'sc3-button-bottom-aligned': index === 0,
              'sc3-button-top-aligned': index > 0
            }"
            @click="removeOverlay(overlay.id)"
          ><i class="fas fa-fw fa-trash-alt"></i></button>
          <IndicatorOverlayDocumentationButton
            :indicator-or-overlay-name="overlay.overlayName"
            :is-first-indicator-overlay-row="index === 0"
          />
        </div>
      </div>
    </div>
    <h3 class="chart-settings-section-title" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value">
      <span>
        <i class="fas fa-fw fa-analytics"></i>
        INDICATORS
      </span>
      <button
        class="sc3-button sc3-button-small sc3-button-link clear-all-button"
        type="button"
        @click="clearAllIndicators()"
      >
        <i class="fas fa-fw fa-ban"></i>
        &nbsp;Clear All Indicators
      </button>
    </h3>
    <div class="drag-n-drop-container" v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value" :class="{ 'drag-n-dropping': isDraggingAnIndicator }">
      <div class="indicators">
        <div
          v-for="(indicator, index) in indicators"
          :key="indicator.id"
          class="indicator-row"
          :class="{ 'row-being-dragged': indicator.id === mouseDownId }"
        >
          <div
            v-if="isDraggingAnIndicator && indicator.id !== mouseDownId && indicator.id !== mouseDownIdBelow && index === 0"
            class="drop-zone top-drop-zone top-drop-zone-extension"
            @mouseup="insertRowAtIndex(index)"
          ></div>
          <div
            v-if="isDraggingAnIndicator && indicator.id !== mouseDownId && indicator.id !== mouseDownIdBelow"
            class="drop-zone top-drop-zone"
            @mouseup="insertRowAtIndex(index)"
          ></div>
          <div
            v-if="isDraggingAnIndicator && indicator.id !== mouseDownId && indicator.id !== mouseDownIdAbove && index === indicators.length - 1"
            class="drop-zone bottom-drop-zone bottom-drop-zone-extension"
            @mouseup="insertRowAtIndex(index + 1)"
          ></div>
          <div
            v-if="isDraggingAnIndicator && indicator.id !== mouseDownId && indicator.id !== mouseDownIdAbove"
            class="drop-zone bottom-drop-zone"
            @mouseup="insertRowAtIndex(index + 1)"
          ></div>
          <span
            class="drag-n-drop-handle"
            :class="{ 'disabled': indicators.length < 2 }"
            @mousedown="handleDragNDropMouseDownIndicator(indicator.id, $event)"
            tabindex="-1"
            title="Drag to reposition indicator"
          ><i class="fas fa-fw fa-line-height" tabindex="-1"></i></span>
          <IndicatorMenu
            :id="indicator.id"
            :should-show-label="index === 0"
            v-model:indicator-name="indicator.indicatorName"
            :indicators="indicators"
            @indicator-menu-changed="handleIndicatorMenuChanged"
          />
          <ParametersInput
            :id="`${indicator.id}`"
            :should-show-label="index === 0"
            v-model:parameters="indicator.parameters"
            :is-overlay-parameters="false"
            :is-indicator-parameters="true"
            :is-indicator-overlay-parameters="false"
            :indicator-or-overlay-name="indicator.indicatorName"
            @parameters-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
            :is-at-least-one-overlay-present="isAtLeastOneOverlayPresent"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
            :is-at-least-one-overlay-of-indicator-present="isAtLeastOneOverlayOfIndicatorPresent"
          />
          <PositionMenu
            :id="indicator.id"
            :should-show-label="index === 0"
            v-model:indicator-position="indicator.indicatorPosition"
            @position-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
          />
          <ReorderButton
            :id="indicator.id"
            :is-up="true"
            :is-first-row="index === 0"
            @reorder="moveIndicatorUp"
          />
          <ReorderButton
            :id="indicator.id"
            :is-up="false"
            :is-first-row="index === 0"
            @reorder="moveIndicatorDown"
          />
          <PriceStyleMenu
            :id="indicator.id"
            :should-show-label="index === 0"
            :is-at-least-one-price-or-price-performance-indicator-present="isAtLeastOnePriceOrPricePerformanceIndicatorPresent"
            v-model:price-style="indicator.indicatorPriceStyle"
            :indicator-name="indicator.indicatorName"
            @price-style-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
          />
          <ColorMenu
            :is-indicator-color-menu="true"
            :id="indicator.id"
            :should-show-label="index === 0"
            :is-at-least-one-non-volume-by-price-overlay-present="isAtLeastOneNonVolumeByPriceOverlayPresent"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
            v-model:color="indicator.indicatorColor"
            :indicator-or-overlay-name="indicator.indicatorName"
            @color-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
          />
          <HeightMenu
            :id="indicator.id"
            :should-show-label="index === 0"
            v-model:height="indicator.indicatorHeight"
            :indicator-name="indicator.indicatorName"
            @height-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
          />
          <IndicatorOpacityMenu
            :id="indicator.id"
            :should-show-label="index === 0"
            v-model:opacity="indicator.indicatorOpacity"
            :indicator-or-overlay-name="indicator.indicatorName"
            @opacity-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
            :is-overlay-opacity-menu="false"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
            :is-at-least-one-overlay-present="isAtLeastOneOverlayPresent"
          />
          <OverlayMenu
            :is-indicator-overlay-menu="true"
            :id="indicator.id"
            :should-show-label="index === 0"
            v-model:overlay-name="indicator.overlayName"
            :indicator-name="indicator.indicatorName"
            :overlays="overlays"
            @overlay-menu-changed="overlayOfIndicatorMenuChanged"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
          />
          <ParametersInput
            :id="`${indicator.id}-overlay`"
            :should-show-label="index === 0"
            v-model:parameters="indicator.overlayParameters"
            :is-overlay-parameters="false"
            :is-indicator-parameters="false"
            :is-indicator-overlay-parameters="true"
            :indicator-or-overlay-name="indicator.indicatorName"
            @parameters-changed="submitSortedIndicatorsAndSortedOverlaysToChartStore"
            :is-at-least-one-overlay-present="isAtLeastOneOverlayPresent"
            :is-at-least-one-non-date-time-axis-indicator-present="isAtLeastOneNonDateTimeAxisIndicatorPresent"
            :is-at-least-one-overlay-of-indicator-present="isAtLeastOneOverlayOfIndicatorPresent"
          />
          <button
            v-show="indicator.indicatorName !== NONE"
            title="Remove Indicator"
            type="button"
            class="sc3-button sc3-button-small sc3-button-red sc3-button-square-medium-height"
            :class="{
              'sc3-button-bottom-aligned': index === 0,
              'sc3-button-top-aligned': index > 0
            }"
            @click="removeIndicator(indicator.id)"
          ><i class="fas fa-fw fa-trash-alt"></i></button>
          <IndicatorOverlayDocumentationButton
            :indicator-or-overlay-name="indicator.indicatorName"
            :is-first-indicator-overlay-row="index === 0"
          />
        </div>
      </div>
    </div>
    <div
      v-if="isDragNDropping"
      class="drag-n-drop-ui-cover"
    ></div>
    <span
      v-if="isDragNDropping"
      :style="dragNDropTrackerCSSStyles"
      id="drag-n-drop-tracker"
    >{{ mouseDownName }}</span>
  </div>
  <div id="indicators-and-overlays-mode-ui-bottom">
    <button
      class="sc3-button sc3-button-blue"
      type="button"
      @click="handleUpdateButtonClicked()"
    >
      <i class="fas fa-fw fa-check-circle" aria-hidden="true"></i>
      &nbsp;Update Chart
    </button>
    <button
      v-show="chartStore.chartType !== CHART_TYPE_OPTION_PERF_CHART.value"
      :disabled="!selectedChartList?.charts.length || selectedChartList.isReadonly"
      class="sc3-button sc3-button-light-grey"
      type="button"
      @click="applyChartStyleToAll()"
    >
      <i class="fas fa-fw fa-cogs" aria-hidden="true"></i>
      &nbsp;Apply ChartStyle to All
    </button>
  </div>
</template>

<script lang="ts" setup>
  import { ref, reactive, computed, nextTick, onMounted, onUnmounted, UnwrapRef } from 'vue';
  import { SubscriptionCallbackMutation } from 'pinia';
  import { useChartStore } from '@/stores/chartStore';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useIndicatorsAndOverlays } from '@/composables/useIndicatorsAndOverlays';
  import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
  import { useRangeSlider } from '@/composables/useRangeSlider';
  import { useActionResultMessages } from '@/composables/useActionResultMessages';
  import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
  import { useModals } from '@/composables/useModals';
  import { useDocumentProperties } from '@/composables/useDocumentProperties';
  import { isValidChartRecid } from '@/composables/isValidChartRecid';
  import { isValidChartStyleRecid } from '@/composables/isValidChartStyleRecid';
  import { loadChartsForChartList } from '@/composables/loadChartsForChartList';
  import { scrollToChart } from '@/utils/scrollToChart';
  import { cloneProxyObject } from '@/utils/cloneProxyObject';
  import IndicatorOverlayDocumentationButton from '@/components/buttons/IndicatorOverlayDocumentationButton.vue';
  import ReorderButton from '@/components/buttons/ReorderButton.vue';
  import AdjustForDividendsCheckbox from '@/components/checkboxes/AdjustForDividendsCheckbox.vue';
  import ArithmeticScaleCheckbox from '@/components/checkboxes/ArithmeticScaleCheckbox.vue';
  import ColorVolumeCheckbox from '@/components/checkboxes/ColorVolumeCheckbox.vue';
  import ExtendedHoursCheckbox from '@/components/checkboxes/ExtendedHoursCheckbox.vue';
  import FullQuoteCheckbox from '@/components/checkboxes/FullQuoteCheckbox.vue';
  import HollowCandlesCheckbox from '@/components/checkboxes/HollowCandlesCheckbox.vue';
  import InvertMainPriceScaleCheckbox from '@/components/checkboxes/InvertMainPriceScaleCheckbox.vue';
  import LogScaleCheckbox from '@/components/checkboxes/LogScaleCheckbox.vue';
  import PriceLabelsCheckbox from '@/components/checkboxes/PriceLabelsCheckbox.vue';
  import SolidCandlesCheckbox from '@/components/checkboxes/SolidCandlesCheckbox.vue';
  import UseMainChartSymbolAsBaselineCheckbox from '@/components/checkboxes/UseMainChartSymbolAsBaselineCheckbox.vue';
  import YAxisLabelsCheckbox from '@/components/checkboxes/YAxisLabelsCheckbox.vue';
  import ZoomThumbnailCheckbox from '@/components/checkboxes/ZoomThumbnailCheckbox.vue';
  import ColorPicker from '@/components/custom-widgets/ColorPicker.vue';
  import DatePicker from '@/components/custom-widgets/DatePicker.vue';
  import ChartSizeMenu from '@/components/select-menus/ChartSizeMenu.vue';
  import ChartTypeMenu from '@/components/select-menus/ChartTypeMenu.vue';
  import ColorMenu from '@/components/select-menus/ColorMenu.vue';
  import ColorSchemeMenu from '@/components/select-menus/ColorSchemeMenu.vue';
  import DownColorMenu from '@/components/select-menus/DownColorMenu.vue';
  import GridStyleMenu from '@/components/select-menus/GridStyleMenu.vue';
  import HeightMenu from '@/components/select-menus/HeightMenu.vue';
  import IndicatorMenu from '@/components/select-menus/IndicatorMenu.vue';
  import IndicatorOpacityMenu from '@/components/select-menus/IndicatorOpacityMenu.vue';
  import KagiPriceFieldMenu from '@/components/select-menus/KagiPriceFieldMenu.vue';
  import KagiReversalUnitMenu from '@/components/select-menus/KagiReversalUnitMenu.vue';
  import LegendsMenu from '@/components/select-menus/LegendsMenu.vue';
  import OpacityMenu from '@/components/select-menus/OpacityMenu.vue';
  import OverlayMenu from '@/components/select-menus/OverlayMenu.vue';
  import PeriodMenu from '@/components/select-menus/PeriodMenu.vue';
  import PositionMenu from '@/components/select-menus/PositionMenu.vue';
  import PriceStyleMenu from '@/components/select-menus/PriceStyleMenu.vue';
  import RangeBarMenu from '@/components/select-menus/RangeBarMenu.vue';
  import RangeDaysMenu from '@/components/select-menus/RangeDaysMenu.vue';
  import RangeGapMenu from '@/components/select-menus/RangeGapMenu.vue';
  import RangeMenu from '@/components/select-menus/RangeMenu.vue';
  import RangeMonthsMenu from '@/components/select-menus/RangeMonthsMenu.vue';
  import RangeYearsMenu from '@/components/select-menus/RangeYearsMenu.vue';
  import RenkoBoxSizeUnitMenu from '@/components/select-menus/RenkoBoxSizeUnitMenu.vue';
  import RenkoPriceFieldMenu from '@/components/select-menus/RenkoPriceFieldMenu.vue';
  import StyleMenu from '@/components/select-menus/StyleMenu.vue';
  import UpColorMenu from '@/components/select-menus/UpColorMenu.vue';
  import VolumeMenu from '@/components/select-menus/VolumeMenu.vue';
  import CustomChartHeightInput from '@/components/text-inputs/CustomChartHeightInput.vue';
  import CustomChartWidthInput from '@/components/text-inputs/CustomChartWidthInput.vue';
  import ExtraBarsInput from '@/components/text-inputs/ExtraBarsInput.vue';
  import KagiReversalInput from '@/components/text-inputs/KagiReversalInput.vue';
  import ParametersInput from '@/components/text-inputs/ParametersInput.vue';
  import PerfChartMainSymbolInput from '@/components/text-inputs/PerfChartMainSymbolInput.vue';
  import PerfChartSymbolInput from '@/components/text-inputs/PerfChartSymbolInput.vue';
  import RenkoBoxSizeInput from '@/components/text-inputs/RenkoBoxSizeInput.vue';
  import { CHART_SIZE_MENU_OPTION_CUSTOM } from '@/enums/chart-size-menu-options';
  import { COLOR_SCHEME_CUSTOM } from '@/enums/color-schemes';
  import { CHART_TYPE_OPTION_PERFORMANCE_PERCENT, CHART_TYPE_OPTION_PERF_CHART, CHART_TYPE_OPTION_KAGI, CHART_TYPE_OPTION_RENKO } from '@/enums/chart-type-options';
  import { CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_BACKGROUND, CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_GRID, CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_TEXT, CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_INDICATOR } from '@/enums/custom-color-scheme-default-colors';
  import { NONE, PRICE, PRICE_PERFORMANCE, INDICATORS_AND_OVERLAYS_ARRAY, DATE_TIME_AXIS, VOLUME_BY_PRICE } from '@/enums/indicators-and-overlays';
  import { INDICATOR_POSITION_ABOVE, INDICATOR_POSITION_BEHIND_PRICE, INDICATOR_POSITION_BEHIND_INDICATOR, INDICATOR_POSITION_BELOW } from '@/enums/indicator-positions';
  import { PerfChartSymbol, Indicator, Overlay, isOverlay } from '@/interfaces/chart';
  import { ChartStore } from '@/types/ChartStore';

  const chartStore = useChartStore();
  const { isLoggedIn } = useAuthentication();
  const {
    MAXIMUM_NUMBER_OF_INDICATORS,
    MAXIMUM_NUMBER_OF_OVERLAYS,
    getBlankIndicator,
    getBlankOverlay,
    formatIndicatorAndOverlayParameters,
    formatPriceIndicatorParameters
  } = useIndicatorsAndOverlays();
  const {
    MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS,
    getBlankPerfChartSymbol,
    formatPerfChartSymbol
  } = usePerfChartSymbols();
  const { createRangeSlider } = useRangeSlider();
  const { addYellowActionResultMessage } = useActionResultMessages();
  const { selectedChartList } = useWorkbenchSelections();
  const { launchCreateAccountModal, launchApplyChartStyleToChartsModal } = useModals();
  const { clientWidth } = useDocumentProperties();
  let lastLatestLoadChartTimeStamp = chartStore.latestLoadChartTimeStamp;
  let lastLatestLoadChartStyleTimeStamp = chartStore.latestLoadChartStyleTimeStamp;
  let lastLatestLoadBrowserTabHistoryChartTimeStamp = chartStore.latestLoadBrowserTabHistoryChartTimeStamp;
  let lastLatestLoadUrlStyleCacheIdParameterChartTimeStamp = chartStore.latestLoadUrlStyleCacheIdParameterChartTimeStamp;
  let lastLatestLoadConvertedPerfChartTimeStamp = chartStore.latestLoadConvertedPerfChartTimeStamp;
  let lastLatestLoadPerfChartTimeStamp = chartStore.latestLoadPerfChartTimeStamp;
  let lastLatestChangeColorSchemeTimeStamp = chartStore.latestChangeColorSchemeTimeStamp;
  const isIncorporatingChartStoreStateIntoComponent = ref(false);
  const perfChartSymbols = ref(cloneProxyObject(chartStore.perfChartSymbols));
  const overlays = ref(cloneProxyObject(chartStore.sortedOverlays));
  const indicators = ref(cloneProxyObject(chartStore.sortedIndicators));
  const isAtLeastOnePriceOrPricePerformanceIndicatorPresent = ref(false);
  const isAtLeastOneNonVolumeByPriceOverlayPresent = ref(false);
  const isAtLeastOneNonDateTimeAxisIndicatorPresent = ref(false);
  const isAtLeastOneOverlayPresent = ref(false);
  const isAtLeastOneOverlayOfIndicatorPresent = ref(false);
  const rangeSliderElementContainer = ref<HTMLSpanElement>();
  const mouseDownId = ref(0);
  const mouseDownName = computed(() => {
    const mouseDownPerfChartSymbol = perfChartSymbols.value.find(perfChartSymbol => perfChartSymbol.id === mouseDownId.value);

    if (mouseDownPerfChartSymbol) {
      return mouseDownPerfChartSymbol.symbol || 'Blank Comparison Symbol Row';
    }

    const mouseDownOverlay = overlays.value.find(overlay => overlay.id === mouseDownId.value);

    if (mouseDownOverlay) {
      if (mouseDownOverlay.parameters) {
        return `${mouseDownOverlay.overlayName}(${mouseDownOverlay.parameters})`;
      }

      return mouseDownOverlay.overlayName;
    }

    const mouseDownIndicator = indicators.value.find(indicator => indicator.id === mouseDownId.value);

    if (mouseDownIndicator) {
      if (mouseDownIndicator.parameters) {
        return `${mouseDownIndicator.indicatorName}(${mouseDownIndicator.parameters})`;
      }

      return mouseDownIndicator.indicatorName;
    }

    return '';
  });
  const mouseDownIdAbove = computed(() => {
    let rowItems;

    if (isDraggingAnOverlay.value) {
      rowItems = overlays.value;
    } else if (isDraggingAnIndicator.value) {
      rowItems = indicators.value;
    } else if (isDraggingAPerfChartSymbol.value) {
      rowItems = perfChartSymbols.value;
    } else {
      return 0;
    }

    const previousRowItem = rowItems.find((rowItem, rowItemIndex) => rowItems.at(rowItemIndex + 1)?.id === mouseDownId.value);

    return previousRowItem?.id ?? 0;
  });
  const mouseDownIdBelow = computed(() => {
    let rowItems;

    if (isDraggingAnOverlay.value) {
      rowItems = overlays.value;
    } else if (isDraggingAnIndicator.value) {
      rowItems = indicators.value;
    } else if (isDraggingAPerfChartSymbol.value) {
      rowItems = perfChartSymbols.value;
    } else {
      return 0;
    }

    const nextRowItem = rowItems.find((rowItem, rowItemIndex) => rowItemIndex > 0 && rowItems.at(rowItemIndex - 1)?.id === mouseDownId.value);

    return nextRowItem?.id ?? 0;
  });
  const dragNDropTrackerCSSStyles = reactive({ left: '', top: '' });
  const isDragNDropping = computed(() => {
    return mouseDownId.value > 0;
  });
  const isDraggingAPerfChartSymbol = computed(() => {
    if (!isDragNDropping.value) {
      return false;
    }

    return !!perfChartSymbols.value.find(perfChartSymbol => perfChartSymbol.id === mouseDownId.value);
  });
  const isDraggingAnOverlay = computed(() => {
    if (!isDragNDropping.value) {
      return false;
    }

    return !!overlays.value.find(overlay => overlay.id === mouseDownId.value);
  });
  const isDraggingAnIndicator = computed(() => {
    if (!isDragNDropping.value) {
      return false;
    }

    return !!indicators.value.find(indicator => indicator.id === mouseDownId.value);
  });

  function initializeRangeSlider() {
    const rangeSliderElement = createRangeSlider();

    rangeSliderElementContainer.value?.appendChild(rangeSliderElement);
  }

  function addPerfChartSymbol(perfChartSymbol: PerfChartSymbol) {
    perfChartSymbols.value.push(perfChartSymbol);
  }

  function addOverlay(overlay: Overlay) {
    overlays.value.push(overlay);
  }

  function addIndicator(indicator: Indicator) {
    indicators.value.push(indicator);
  }

  function removePerfChartSymbol(perfChartSymbolId: number) {
    perfChartSymbols.value.splice(perfChartSymbols.value.findIndex(perfChartSymbol => perfChartSymbol.id === perfChartSymbolId), 1);
    perfChartSymbolInputChanged();
    submitPerfChartSymbolsToChartStore();
  }

  function removeOverlay(overlayId: number) {
    overlays.value.splice(overlays.value.findIndex(overlay => overlay.id === overlayId), 1);
    overlayMenuChanged();
    updateIsAtLeastOneNonVolumeByPriceOverlayPresent();
    updateIsAtLeastOneOverlayPresent();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function removeIndicator(indicatorId: number) {
    indicators.value.splice(indicators.value.findIndex(indicator => indicator.id === indicatorId), 1);
    indicatorMenuChanged();
    updateIsAtLeastOnePriceOrPricePerformanceIndicatorPresent();
    updateIsAtLeastOneNonDateTimeAxisIndicatorPresent();
    updateIsAtLeastOneOverlayOfIndicatorPresent();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function insertRowAtIndex(newRowIndex: number) {
    let rowItems;

    if (isDraggingAnOverlay.value) {
      rowItems = overlays.value;
    } else if (isDraggingAnIndicator.value) {
      rowItems = indicators.value;
    } else if (isDraggingAPerfChartSymbol.value) {
      rowItems = perfChartSymbols.value;
    } else {
      return;
    }

    const currentRowIndex = rowItems.findIndex(rowItem => rowItem.id === mouseDownId.value);

    if (newRowIndex === currentRowIndex) {
      return;
    }

    const currentRowItem = rowItems.at(currentRowIndex);

    if (!currentRowItem) {
      return;
    }

    insertArrayElementAtIndex(rowItems, currentRowItem, newRowIndex, currentRowIndex);

    if (isDraggingAPerfChartSymbol.value) {
      perfChartSymbolInputChanged();
      submitPerfChartSymbolsToChartStore();
      return;
    }

    if (isDraggingAnOverlay.value) {
      overlayMenuChanged()
    } else {
      indicatorMenuChanged();
    }

    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function insertArrayElementAtIndex<T>(array: T[], element: T, newIndex: number, currentIndex: number) {
    array.splice(currentIndex, 1);

    if (currentIndex > newIndex) {
      array.splice(newIndex, 0, element);
    } else {
      array.splice(newIndex - 1, 0, element);
    }
  }

  function perfChartSymbolInputChanged() {
    removeBlankPerfChartSymbolRows();
    ensureMaximumNumberOfPerfChartSymbolRowsNotExceeded();
    ensureMinimumNumberOfPerfChartSymbolRowsIsMet();
    ensureTheresAtLeastOneBlankPerfChartSymbolRow();
  }

  function handlePerfChartSymbolInputChanged() {
    perfChartSymbolInputChanged();
    submitPerfChartSymbolsToChartStore();
  }

  function overlayMenuChanged(overlayId?: number) {
    removeBlankOverlayRows();
    autofillDefaultIndicatorOrOverlayParameters(false, overlayId);
    ensureMaximumNumberOfOverlayRowsNotExceeded();
    ensureMinimumNumberOfOverlayRowsIsMet();
    ensureTheresAtLeastOneBlankOverlayRow();
  }

  function handleOverlayMenuChanged(overlayId: number) {
    overlayMenuChanged(overlayId);
    updateIsAtLeastOneNonVolumeByPriceOverlayPresent();
    updateIsAtLeastOneOverlayPresent();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function overlayOfIndicatorMenuChanged(indicatorId: number) {
    autofillDefaultIndicatorOrOverlayParameters(true, indicatorId);
    updateIsAtLeastOneOverlayOfIndicatorPresent();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function indicatorMenuChanged(indicatorId?: number) {
    removeBlankIndicatorRows();
    autofillDefaultIndicatorOrOverlayParameters(false, indicatorId);
    ensureMaximumNumberOfIndicatorRowsNotExceeded();
    ensureMinimumNumberOfIndicatorRowsIsMet();
    ensureTheresAtLeastOneBlankIndicatorRow();
  }

  function handleIndicatorMenuChanged(indicatorId: number) {
    indicatorMenuChanged(indicatorId);
    updateIsAtLeastOnePriceOrPricePerformanceIndicatorPresent();
    updateIsAtLeastOneNonDateTimeAxisIndicatorPresent();
    updateIsAtLeastOneOverlayOfIndicatorPresent();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function autofillDefaultIndicatorOrOverlayParameters(shouldAutofillOverlayOfIndicator: boolean, id?: number) {
    if (!id || isIncorporatingChartStoreStateIntoComponent.value) {
      return;
    }

    let indicatorOrOverlay: Indicator | Overlay | undefined = indicators.value.find(indicator => indicator.id === id);

    if (shouldAutofillOverlayOfIndicator && !indicatorOrOverlay) {
      return;
    }

    if (!indicatorOrOverlay) {
      indicatorOrOverlay = overlays.value.find(overlay => overlay.id === id);
    }

    if (!indicatorOrOverlay) {
      return;
    }

    if (isOverlay(indicatorOrOverlay)) {
      const overlayDefinition = INDICATORS_AND_OVERLAYS_ARRAY.find(definition => definition.name === indicatorOrOverlay.overlayName);
      indicatorOrOverlay.parameters = overlayDefinition ? overlayDefinition.defaultParameters : '';
      return;
    }

    const definitionName = shouldAutofillOverlayOfIndicator ? indicatorOrOverlay.overlayName : indicatorOrOverlay.indicatorName;
    const indicatorOrOverlayDefinition = INDICATORS_AND_OVERLAYS_ARRAY.find(definition => definition.name === definitionName);
    const indicatorOrOverlayParametersField = shouldAutofillOverlayOfIndicator ? 'overlayParameters' : 'parameters';
    indicatorOrOverlay[indicatorOrOverlayParametersField] = indicatorOrOverlayDefinition ? indicatorOrOverlayDefinition.defaultParameters : '';
  }

  function removeBlankPerfChartSymbolRows() {
    perfChartSymbols.value = perfChartSymbols.value.filter(perfChartSymbol => perfChartSymbol.symbol.replace(/\s/g, ''));
  }

  function ensureTheresAtLeastOneBlankPerfChartSymbolRow() {
    if (perfChartSymbols.value.length >= MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS) {
      return;
    }

    if (
      !perfChartSymbols.value.length ||
      perfChartSymbols.value.at(-1)?.symbol.replace(/\s/g, '')
    ) {
      const blankPerfChartSymbol = getBlankPerfChartSymbol();

      addPerfChartSymbol(blankPerfChartSymbol);
    }
  }

  function ensureMinimumNumberOfPerfChartSymbolRowsIsMet() {
    for (let i = perfChartSymbols.value.length; i < 3; i++) {
      const blankPerfChartSymbol = getBlankPerfChartSymbol();

      addPerfChartSymbol(blankPerfChartSymbol);
    }
  }

  function removeBlankOverlayRows() {
    overlays.value = overlays.value.filter(overlay => overlay?.overlayName && overlay.overlayName !== NONE);
  }

  function ensureTheresAtLeastOneBlankOverlayRow() {
    if (overlays.value.length >= MAXIMUM_NUMBER_OF_OVERLAYS) {
      return;
    }

    if (
      !overlays.value.length ||
      overlays.value.at(-1)?.overlayName !== NONE
    ) {
      const blankOverlay = getBlankOverlay();

      addOverlay(blankOverlay);
    }
  }

  function ensureMinimumNumberOfOverlayRowsIsMet() {
    for (let i = overlays.value.length; i < 3; i++) {
      const blankOverlay = getBlankOverlay();

      addOverlay(blankOverlay);
    }
  }

  function removeBlankIndicatorRows() {
    indicators.value = indicators.value.filter(indicator => indicator?.indicatorName && indicator.indicatorName !== NONE);
  }

  function ensureTheresAtLeastOneBlankIndicatorRow() {
    if (indicators.value.length >= MAXIMUM_NUMBER_OF_INDICATORS) {
      return;
    }

    if (
      !indicators.value.length ||
      indicators.value.at(-1)?.indicatorName !== NONE
    ) {
      const blankIndicator = getBlankIndicator();

      addIndicator(blankIndicator);
    }
  }

  function ensureMinimumNumberOfIndicatorRowsIsMet() {
    for (let i = indicators.value.length; i < 3; i++) {
      const blankIndicator = getBlankIndicator();

      addIndicator(blankIndicator);
    }
  }

  function ensureMaximumNumberOfPerfChartSymbolRowsNotExceeded() {
    perfChartSymbols.value.splice(MAXIMUM_NUMBER_OF_PERF_CHART_SYMBOLS, perfChartSymbols.value.length);
  }

  function ensureMaximumNumberOfOverlayRowsNotExceeded() {
    overlays.value.splice(MAXIMUM_NUMBER_OF_OVERLAYS, overlays.value.length);
  }

  function ensureMaximumNumberOfIndicatorRowsNotExceeded() {
    indicators.value.splice(MAXIMUM_NUMBER_OF_INDICATORS, indicators.value.length);
  }

  function stopDragNDrop() {
    mouseDownId.value = 0;
  }

  function setDragNDropTrackerCoordinates(e: MouseEvent) {
    dragNDropTrackerCSSStyles.left = (e.clientX + 16) + 'px';
    dragNDropTrackerCSSStyles.top = e.clientY + 'px';
  }

  function handleDragNDropMouseDown(id: number, e: MouseEvent) {
    mouseDownId.value = id;

    setDragNDropTrackerCoordinates(e);
  }

  function handleDragNDropMouseDownPerfChartSymbol(id: number, e: MouseEvent) {
    if (perfChartSymbols.value.length < 2) {
      return;
    }

    handleDragNDropMouseDown(id, e);
  }

  function handleDragNDropMouseDownOverlay(id: number, e: MouseEvent) {
    if (overlays.value.length < 2) {
      return;
    }

    handleDragNDropMouseDown(id, e);
  }

  function handleDragNDropMouseDownIndicator(id: number, e: MouseEvent) {
    if (indicators.value.length < 2) {
      return;
    }

    handleDragNDropMouseDown(id, e);
  }

  function handleMouseUp(e: MouseEvent) {
    if (!isDragNDropping.value) {
      return;
    }

    window.setTimeout(() => {
      stopDragNDrop();
      setDragNDropTrackerCoordinates(e);
    }, 0);
  }

  function handleMouseMove(e: MouseEvent) {
    if (!isDragNDropping.value) {
      return;
    }

    setDragNDropTrackerCoordinates(e);
  }

  function movePerfChartSymbolUp(perfChartSymbolId: number) {
    const currentPerfChartSymbolIndex = perfChartSymbols.value.findIndex(perfChartSymbol => perfChartSymbol.id === perfChartSymbolId);

    if (currentPerfChartSymbolIndex === 0) {
      return;
    }

    const currentPerfChartSymbol = perfChartSymbols.value.at(currentPerfChartSymbolIndex);
    const previousPerfChartSymbol = perfChartSymbols.value.at(currentPerfChartSymbolIndex - 1);

    if (!currentPerfChartSymbol || !previousPerfChartSymbol) {
      return;
    }

    perfChartSymbols.value.splice(currentPerfChartSymbolIndex - 1, 2, currentPerfChartSymbol, previousPerfChartSymbol);
    perfChartSymbolInputChanged();
    submitPerfChartSymbolsToChartStore();
  }

  function movePerfChartSymbolDown(perfChartSymbolId: number) {
    const currentPerfChartSymbolIndex = perfChartSymbols.value.findIndex(perfChartSymbol => perfChartSymbol.id === perfChartSymbolId);

    if (currentPerfChartSymbolIndex === perfChartSymbols.value.length - 1) {
      return;
    }

    const nextPerfChartSymbol = perfChartSymbols.value.at(currentPerfChartSymbolIndex + 1);
    const currentPerfChartSymbol = perfChartSymbols.value.at(currentPerfChartSymbolIndex);

    if (!nextPerfChartSymbol || !currentPerfChartSymbol) {
      return;
    }

    perfChartSymbols.value.splice(currentPerfChartSymbolIndex, 2, nextPerfChartSymbol, currentPerfChartSymbol);
    perfChartSymbolInputChanged();
    submitPerfChartSymbolsToChartStore();
  }

  function moveOverlayUp(overlayId: number) {
    const currentOverlayIndex = overlays.value.findIndex(overlay => overlay.id === overlayId);

    if (currentOverlayIndex === 0) {
      return;
    }

    const currentOverlay = overlays.value.at(currentOverlayIndex);
    const previousOverlay = overlays.value.at(currentOverlayIndex - 1);

    if (!currentOverlay || !previousOverlay) {
      return;
    }

    overlays.value.splice(currentOverlayIndex - 1, 2, currentOverlay, previousOverlay);
    overlayMenuChanged();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function moveOverlayDown(overlayId: number) {
    const currentOverlayIndex = overlays.value.findIndex(overlay => overlay.id === overlayId);

    if (currentOverlayIndex === overlays.value.length - 1) {
      return;
    }

    const nextOverlay = overlays.value.at(currentOverlayIndex + 1);
    const currentOverlay = overlays.value.at(currentOverlayIndex);

    if (!nextOverlay || !currentOverlay) {
      return;
    }

    overlays.value.splice(currentOverlayIndex, 2, nextOverlay, currentOverlay);
    overlayMenuChanged();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function moveIndicatorUp(indicatorId: number) {
    const currentIndicatorIndex = indicators.value.findIndex(indicator => indicator.id === indicatorId);

    if (currentIndicatorIndex === 0) {
      return;
    }

    const currentIndicator = indicators.value.at(currentIndicatorIndex);
    const previousIndicator = indicators.value.at(currentIndicatorIndex - 1);

    if (!currentIndicator || !previousIndicator) {
      return;
    }

    indicators.value.splice(currentIndicatorIndex - 1, 2, currentIndicator, previousIndicator);
    indicatorMenuChanged();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function moveIndicatorDown(indicatorId: number) {
    const currentIndicatorIndex = indicators.value.findIndex(indicator => indicator.id === indicatorId);

    if (currentIndicatorIndex === indicators.value.length - 1) {
      return;
    }

    const nextIndicator = indicators.value.at(currentIndicatorIndex + 1);
    const currentIndicator = indicators.value.at(currentIndicatorIndex);

    if (!nextIndicator || !currentIndicator) {
      return;
    }

    indicators.value.splice(currentIndicatorIndex, 2, nextIndicator, currentIndicator);
    indicatorMenuChanged();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function formatPerfChartSymbols() {
    removeBlankPerfChartSymbolRows();

    for (const perfChartSymbol of perfChartSymbols.value) {
      perfChartSymbol.symbol = formatPerfChartSymbol(perfChartSymbol.symbol);
    }

    perfChartSymbolInputChanged();
  }

  function sortAndFormatIndicatorsAndOverlays() {
    removeBlankOverlayRows();
    removeBlankIndicatorRows();

    for (const indicator of indicators.value) {
      if (indicator.indicatorName === DATE_TIME_AXIS.name && indicator.indicatorPosition === INDICATOR_POSITION_BEHIND_PRICE.title) {
        indicator.indicatorPosition = INDICATOR_POSITION_BELOW.title;
      }
    }

    const firstIndicator = indicators.value.at(0);

    if (firstIndicator?.indicatorPosition === INDICATOR_POSITION_BEHIND_INDICATOR.title) {
      firstIndicator.indicatorPosition = INDICATOR_POSITION_ABOVE.title;
    }

    const indicatorsAbovePrice: Indicator[] = [];
    let lastIndicatorWasAbovePrice = false;

    for (const indicator of indicators.value) {
      lastIndicatorWasAbovePrice = indicator.indicatorPosition === INDICATOR_POSITION_ABOVE.title || (indicator.indicatorPosition === INDICATOR_POSITION_BEHIND_INDICATOR.title && lastIndicatorWasAbovePrice);

      if (lastIndicatorWasAbovePrice) {
        indicatorsAbovePrice.push(indicator);
      }
    }

    const indicatorsBehindPrice: Indicator[] = [];
    let lastIndicatorWasBehindPrice = false;

    for (const indicator of indicators.value) {
      if (indicatorsAbovePrice.includes(indicator)) {
        lastIndicatorWasBehindPrice = false;
        continue;
      }

      lastIndicatorWasBehindPrice = indicator.indicatorPosition === INDICATOR_POSITION_BEHIND_PRICE.title || (indicator.indicatorPosition === INDICATOR_POSITION_BEHIND_INDICATOR.title && lastIndicatorWasBehindPrice);

      if (lastIndicatorWasBehindPrice) {
        indicator.indicatorPosition = INDICATOR_POSITION_BEHIND_PRICE.title;

        indicatorsBehindPrice.push(indicator);
      }
    }

    const indicatorsBelowPrice = indicators.value.filter(indicator => !indicatorsAbovePrice.includes(indicator) && !indicatorsBehindPrice.includes(indicator));

    indicators.value = [
      ...indicatorsAbovePrice,
      ...indicatorsBehindPrice,
      ...indicatorsBelowPrice
    ];

    for (const overlay of overlays.value) {
      overlay.parameters = formatIndicatorAndOverlayParameters(overlay.parameters);
    }

    for (const indicator of indicators.value) {
      indicator.parameters = formatIndicatorAndOverlayParameters(indicator.parameters);
      indicator.overlayParameters = formatIndicatorAndOverlayParameters(indicator.overlayParameters);
      const definition = INDICATORS_AND_OVERLAYS_ARRAY.find(option => option.name === indicator.indicatorName);

      if (
        definition === PRICE ||
        definition === PRICE_PERFORMANCE
      ) {
        indicator.parameters = formatPriceIndicatorParameters(indicator.parameters);
      }
    }

    overlayMenuChanged();
    indicatorMenuChanged();
  }

  function submitPerfChartSymbolsToChartStore() {
    if (isIncorporatingChartStoreStateIntoComponent.value) {
      return;
    }

    formatPerfChartSymbols();

    const clonedPerfChartSymbols = cloneProxyObject(perfChartSymbols.value)
      .filter(perfChartSymbol => perfChartSymbol.symbol);

    chartStore.$patch({
      perfChartSymbols: clonedPerfChartSymbols
    });
  }

  function submitSortedIndicatorsAndSortedOverlaysToChartStore() {
    if (isIncorporatingChartStoreStateIntoComponent.value) {
      return;
    }

    sortAndFormatIndicatorsAndOverlays();

    const clonedIndicators = cloneProxyObject(indicators.value)
      .filter(indicator => indicator.indicatorName && indicator.indicatorName !== NONE);

    const clonedOverlays = cloneProxyObject(overlays.value)
      .filter(overlay => overlay.overlayName && overlay.overlayName !== NONE);

    chartStore.$patch({
      sortedIndicators: clonedIndicators,
      sortedOverlays: clonedOverlays
    });
  }

  function clearAllPerfChartSymbols() {
    perfChartSymbols.value.splice(
      0,
      perfChartSymbols.value.length
    );
    ensureMinimumNumberOfPerfChartSymbolRowsIsMet();
    submitPerfChartSymbolsToChartStore();
  }

  function clearAllOverlays() {
    overlays.value.splice(
      0,
      overlays.value.length
    );
    ensureMinimumNumberOfOverlayRowsIsMet();
    updateIsAtLeastOneNonVolumeByPriceOverlayPresent();
    updateIsAtLeastOneOverlayPresent();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function clearAllIndicators() {
    indicators.value.splice(
      0,
      indicators.value.length
    );
    ensureMinimumNumberOfIndicatorRowsIsMet();
    updateIsAtLeastOnePriceOrPricePerformanceIndicatorPresent();
    updateIsAtLeastOneNonDateTimeAxisIndicatorPresent();
    updateIsAtLeastOneOverlayOfIndicatorPresent();
    submitSortedIndicatorsAndSortedOverlaysToChartStore();
  }

  function updateIsAtLeastOnePriceOrPricePerformanceIndicatorPresent() {
    for (const indicator of indicators.value) {
      if (
        indicator &&
        (indicator.indicatorName === PRICE.name || indicator.indicatorName === PRICE_PERFORMANCE.name)
      ) {
        isAtLeastOnePriceOrPricePerformanceIndicatorPresent.value = true;
        return;
      }
    }

    isAtLeastOnePriceOrPricePerformanceIndicatorPresent.value = false;
  }

  function updateIsAtLeastOneNonVolumeByPriceOverlayPresent() {
    for (const overlay of overlays.value) {
      if (
        overlay?.overlayName &&
        overlay.overlayName !== NONE &&
        overlay.overlayName !== VOLUME_BY_PRICE.name
      ) {
        isAtLeastOneNonVolumeByPriceOverlayPresent.value = true;
        return;
      }
    }

    isAtLeastOneNonVolumeByPriceOverlayPresent.value = false;
  }

  function updateIsAtLeastOneNonDateTimeAxisIndicatorPresent() {
    for (const indicator of indicators.value) {
      if (
        indicator?.indicatorName &&
        indicator.indicatorName !== NONE &&
        indicator.indicatorName !== DATE_TIME_AXIS.name
      ) {
        isAtLeastOneNonDateTimeAxisIndicatorPresent.value = true;
        return;
      }
    }

    isAtLeastOneNonDateTimeAxisIndicatorPresent.value = false;
  }

  function updateIsAtLeastOneOverlayPresent() {
    for (const overlay of overlays.value) {
      if (
        overlay?.overlayName &&
        overlay.overlayName !== NONE
      ) {
        isAtLeastOneOverlayPresent.value = true;
        return;
      }
    }

    isAtLeastOneOverlayPresent.value = false;
  }

  function updateIsAtLeastOneOverlayOfIndicatorPresent() {
    for (const indicator of indicators.value) {
      if (
        indicator?.indicatorName &&
        indicator.indicatorName !== NONE &&
        indicator.indicatorName !== DATE_TIME_AXIS.name &&
        indicator.overlayName &&
        indicator.overlayName !== NONE
      ) {
        isAtLeastOneOverlayOfIndicatorPresent.value = true;
        return;
      }
    }

    isAtLeastOneOverlayOfIndicatorPresent.value = false;
  }

  async function handleUpdateButtonClicked() {
    if (chartStore.chartType === CHART_TYPE_OPTION_PERF_CHART.value) {
      submitPerfChartSymbolsToChartStore();
    } else {
      submitSortedIndicatorsAndSortedOverlaysToChartStore();
    }

    await scrollToChart();
  }

  async function applyChartStyleToAll() {
    if (!isLoggedIn.value) {
      launchCreateAccountModal();
      return;
    }

    const chartList = selectedChartList.value;

    if (!chartList) {
      window.sc3WorkbenchLogger.error(`Failed to launch the ApplyChartStyleToCharts modal from the ChartSettingsModeUI - selectedChartList is falsy: ${chartList}.`);

      addYellowActionResultMessage('Please select a ChartList.');
      return;
    }

    await loadChartsForChartList(chartList.recid, chartList.listNum, true);

    launchApplyChartStyleToChartsModal(chartList, chartList.charts);
  }

  async function handleChartStoreChanged(mutation: SubscriptionCallbackMutation<ChartStore>, state: UnwrapRef<ChartStore>) {
    const hasLatestLoadChartTimeStampChanged = lastLatestLoadChartTimeStamp !== state.latestLoadChartTimeStamp;
    lastLatestLoadChartTimeStamp = state.latestLoadChartTimeStamp;
    const hasLatestLoadChartStyleTimeStampChanged = lastLatestLoadChartStyleTimeStamp !== state.latestLoadChartStyleTimeStamp;
    lastLatestLoadChartStyleTimeStamp = state.latestLoadChartStyleTimeStamp;
    const hasLatestLoadBrowserTabHistoryChartTimeStampChanged = lastLatestLoadBrowserTabHistoryChartTimeStamp !== state.latestLoadBrowserTabHistoryChartTimeStamp;
    lastLatestLoadBrowserTabHistoryChartTimeStamp = state.latestLoadBrowserTabHistoryChartTimeStamp;
    const hasLatestLoadUrlStyleCacheIdParameterChartTimeStampChanged = lastLatestLoadUrlStyleCacheIdParameterChartTimeStamp !== state.latestLoadUrlStyleCacheIdParameterChartTimeStamp;
    lastLatestLoadUrlStyleCacheIdParameterChartTimeStamp = state.latestLoadUrlStyleCacheIdParameterChartTimeStamp;
    const hasLatestLoadConvertedPerfChartTimeStampChanged = lastLatestLoadConvertedPerfChartTimeStamp !== state.latestLoadConvertedPerfChartTimeStamp;
    lastLatestLoadConvertedPerfChartTimeStamp = state.latestLoadConvertedPerfChartTimeStamp;
    const hasLatestLoadPerfChartTimeStampChanged = lastLatestLoadPerfChartTimeStamp !== state.latestLoadPerfChartTimeStamp;
    lastLatestLoadPerfChartTimeStamp = state.latestLoadPerfChartTimeStamp;
    const hasLatestChangeColorSchemeTimeStampChanged = lastLatestChangeColorSchemeTimeStamp !== state.latestChangeColorSchemeTimeStamp;
    lastLatestChangeColorSchemeTimeStamp = state.latestChangeColorSchemeTimeStamp;
    const isChartBeingLoaded =
      hasLatestLoadChartTimeStampChanged &&
      isValidChartRecid(state.recidOfChartBeingViewed);
    const isChartStyleBeingLoaded =
      !isChartBeingLoaded &&
      hasLatestLoadChartStyleTimeStampChanged &&
      isValidChartStyleRecid(state.recidOfChartStyleBeingViewed, true);
    const isSavedChartOrChartStyleBeingLoaded = isChartBeingLoaded || isChartStyleBeingLoaded;
    const isBrowserTabHistoryChartBeingLoaded = !isSavedChartOrChartStyleBeingLoaded && hasLatestLoadBrowserTabHistoryChartTimeStampChanged;
    const isUrlStyleCacheIdParameterChartBeingLoaded = !isSavedChartOrChartStyleBeingLoaded && !hasLatestLoadBrowserTabHistoryChartTimeStampChanged && hasLatestLoadUrlStyleCacheIdParameterChartTimeStampChanged;
    const isConvertedPerfChartBeingLoaded = !isSavedChartOrChartStyleBeingLoaded && !hasLatestLoadBrowserTabHistoryChartTimeStampChanged && !hasLatestLoadUrlStyleCacheIdParameterChartTimeStampChanged && hasLatestLoadConvertedPerfChartTimeStampChanged;
    const isPerfChartBeingLoaded = !isSavedChartOrChartStyleBeingLoaded && !hasLatestLoadBrowserTabHistoryChartTimeStampChanged && !hasLatestLoadUrlStyleCacheIdParameterChartTimeStampChanged && !hasLatestLoadConvertedPerfChartTimeStampChanged && hasLatestLoadPerfChartTimeStampChanged;
    isIncorporatingChartStoreStateIntoComponent.value = isSavedChartOrChartStyleBeingLoaded || isBrowserTabHistoryChartBeingLoaded || isUrlStyleCacheIdParameterChartBeingLoaded || isConvertedPerfChartBeingLoaded || hasLatestChangeColorSchemeTimeStampChanged || isPerfChartBeingLoaded;

    if (!isIncorporatingChartStoreStateIntoComponent.value) {
      return;
    }

    perfChartSymbols.value = cloneProxyObject(state.perfChartSymbols);
    overlays.value = cloneProxyObject(state.sortedOverlays);
    indicators.value = cloneProxyObject(state.sortedIndicators);

    formatPerfChartSymbols();
    sortAndFormatIndicatorsAndOverlays();
    updateIsAtLeastOnePriceOrPricePerformanceIndicatorPresent();
    updateIsAtLeastOneNonVolumeByPriceOverlayPresent();
    updateIsAtLeastOneNonDateTimeAxisIndicatorPresent();
    updateIsAtLeastOneOverlayPresent();
    updateIsAtLeastOneOverlayOfIndicatorPresent();

    await nextTick();

    isIncorporatingChartStoreStateIntoComponent.value = false;
  }

  chartStore.$subscribe(handleChartStoreChanged);

  updateIsAtLeastOnePriceOrPricePerformanceIndicatorPresent();
  updateIsAtLeastOneNonVolumeByPriceOverlayPresent();
  updateIsAtLeastOneNonDateTimeAxisIndicatorPresent();
  updateIsAtLeastOneOverlayPresent();
  updateIsAtLeastOneOverlayOfIndicatorPresent();
  submitPerfChartSymbolsToChartStore();
  submitSortedIndicatorsAndSortedOverlaysToChartStore();

  onMounted(() => {
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);

    initializeRangeSlider();

    onUnmounted(() => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    });
  });
</script>

<style scoped>
  * {
    --white-space-gap-between-indicator-and-overlay-rows: 2px;
    --drop-zone-height: calc((var(--sc3-compact-height) / 2) + var(--white-space-gap-between-indicator-and-overlay-rows));
  }

  #chart-settings-container,
  #indicators-and-overlays-container {
    margin: 6px;
  }

  #attributes-container {
    display: flex;
    align-items: center;
    gap: 0px 12px;
  }

  .drag-n-drop-container {
    position: relative;
    white-space: nowrap;
    overflow-x: auto;
    margin: 6px;
  }

  .drag-n-drop-container.drag-n-dropping {
    overflow: visible;
  }

  .drag-n-drop-container.drag-n-dropping > div {
    width: fit-content;
    background-color: white;
  }

  #indicators-and-overlays-mode-ui-bottom {
    margin: 0px;
    padding: 6px;
    border-top: 1px solid #c0ced8;
  }

  .chart-settings-section-title {
    margin: 6px 6px 6px 2px;
    padding: 0px 0px 0px 2px;
    border-bottom: 2px solid #c0ced8;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    line-height: 1.5;
    font-size: 14px;
    font-weight: 700;
    color: #2d3339;
    position: relative;
    overflow: hidden;
  }

  .clear-all-button {
    margin: 0px;
    float: right;
  }

  .ui-mini-container-row {
    margin-bottom: 4px;
    font-size: 0px;
  }

  .ui-mini-container-row-last {
    margin-bottom: 0px;
  }

  .ui-mini-container-row-right-aligned {
    text-align: right;
  }

  .ui-mini-container {
    display: inline-block;
    vertical-align: top;
    margin: 0px;
  }

  .ui-mini-checkbox-container {
    display: inline-flex;
    flex-direction: column;
    gap: 2px;
    padding: 2px 6px 2px 0px;
    margin: 0px 10px 0px 4px;
    border-bottom: 1px solid #c0ced8;
  }

  .chart-settings-checkbox-container {
    display: grid;
    grid-template-rows: auto auto;
    grid-template-columns: auto auto auto;
    grid-auto-flow: column;
    border: 1px solid #c0ced8;
    border-radius: 4px;
    background-color: white;
    padding: 2px;
  }

  .perf-chart-symbols,
  .overlays,
  .indicators {
    display: grid;
    border: none;
    padding: 0px 6px 6px 6px;
  }

  .perf-chart-symbol-row:not(:last-child),
  .overlay-row:not(:last-child),
  .indicator-row:not(:last-child) {
    margin-bottom: var(--white-space-gap-between-indicator-and-overlay-rows);
  }

  .perf-chart-symbol-row,
  .overlay-row,
  .indicator-row {
    display: flex;
    align-items: end;
    position: relative;
    gap: 2px;
  }

  .perf-chart-symbol-row > *,
  .overlay-row > *,
  .indicator-row > * {
    flex-shrink: 0;
  }

  .row-being-dragged {
    opacity: 0.25;
  }

  .drop-zone {
    position: absolute;
    left: 0px;
    right: 0px;
    height: var(--drop-zone-height);
    z-index: 1001;
    cursor: copy !important;
  }

  .top-drop-zone {
    bottom: calc((-1 * var(--white-space-gap-between-indicator-and-overlay-rows)) + var(--drop-zone-height));
    border-top: var(--white-space-gap-between-indicator-and-overlay-rows) solid transparent;
  }

  .bottom-drop-zone {
    bottom: calc(-1 * var(--white-space-gap-between-indicator-and-overlay-rows));
    border-bottom: var(--white-space-gap-between-indicator-and-overlay-rows) solid transparent;
  }

  .top-drop-zone:hover,
  .top-drop-zone-extension:hover + .top-drop-zone,
  .bottom-drop-zone:hover,
  .bottom-drop-zone-extension:hover + .bottom-drop-zone {
    border-color: #f8d23a;
  }

  .top-drop-zone-extension,
  .bottom-drop-zone-extension {
    height: calc(var(--drop-zone-height) + 60px);
    border-top: none;
    border-bottom: none;
  }

  .bottom-drop-zone-extension {
    bottom: calc(var(--white-space-gap-between-indicator-and-overlay-rows) - 60px);
  }

  .drag-n-drop-handle {
    height: var(--sc3-compact-height);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0px 2px 0px 0px;
    margin: 0px;
    color: #707f8f;
    cursor: grab;
    user-select: none;
  }

  .drag-n-drop-handle,
  .drag-n-drop-handle i {
    outline: none;
  }

  .drag-n-drop-handle:hover {
    color: #434c56;
  }

  .drag-n-drop-handle.disabled {
    color: #c0ced8;
    cursor: default;
  }

  #drag-n-drop-tracker {
    position: fixed;
    z-index: 2000;
    background-color: #133353;
    color: white;
    font-size: 14px;
    font-weight: 900;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    padding: 4px;
    margin: 0px;
    border: none;
    border-radius: 5px;
  }

  .drag-n-drop-ui-cover {
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    cursor: grabbing;
    z-index: 1000;
  }

  @media (max-width: 1270px) {
    #attributes-container {
      flex-direction: column;
      align-items: start;
    }
  }

  @media (min-width: 575px) {
    .chart-settings-range-ui-line-break {
      display: none;
    }
  }

  .screen-size-small-tablet .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) {
    grid-template-rows: auto auto auto;
    grid-template-columns: auto auto;
  }

  .screen-size-phone .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) {
    grid-template-rows: auto auto auto auto auto auto;
    grid-template-columns: auto;
  }

  .screen-size-extra-large-desktop .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) .ui-mini-checkbox-container:nth-child(2n),
  .screen-size-large-desktop .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) .ui-mini-checkbox-container:nth-child(2n),
  .screen-size-medium-desktop .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) .ui-mini-checkbox-container:nth-child(2n),
  .screen-size-small-desktop .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) .ui-mini-checkbox-container:nth-child(2n),
  .screen-size-extra-small-desktop .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) .ui-mini-checkbox-container:nth-child(2n),
  .screen-size-tablet .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) .ui-mini-checkbox-container:nth-child(2n),
  .screen-size-small-tablet .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) .ui-mini-checkbox-container:nth-child(3n),
  .screen-size-phone .chart-settings-checkbox-container:not(.chart-settings-checkbox-container-perf-chart) .ui-mini-checkbox-container:nth-child(6n) {
    border-bottom: none;
  }

  .chart-settings-checkbox-container-perf-chart {
    grid-template-rows: auto auto auto;
    grid-template-columns: auto;
  }

  .chart-settings-checkbox-container-perf-chart .ui-mini-checkbox-container {
    border-bottom: none;
  }
</style>
